import { api } from '../../lib/api.js';
import { prefabInstanceOf, prefabRef } from '../../lib/prefab.js';
import { useAssets } from '../../state/assets.js';
import { logEditor } from '../../state/console.js';
import { usePrefabs } from '../../state/prefabs.js';
import { useProjectFiles } from '../../state/project-files.js';
import { useScene } from '../../state/scene.js';
import { useSceneSet } from '../../state/scene-set.js';
import { useWorkspace } from '../../state/workspace.js';
import type { MenuItem } from '../ui/controls.js';

const MANIFEST = 'prism.project.json';

/** Makes `path` the scene the game starts with (`startScene` in prism.project.json). */
async function setStartScene(path: string): Promise<void> {
  const projectId = useWorkspace.getState().projectId;
  if (!projectId) return;
  try {
    const res = await api.get<{ file: { content: string } }>(`/projects/${projectId}/file?path=${MANIFEST}`);
    const manifest = JSON.parse(res.file.content) as Record<string, unknown>;
    manifest.startScene = path;
    await api.put(`/projects/${projectId}/file`, { path: MANIFEST, content: `${JSON.stringify(manifest, null, 2)}\n` });
    useProjectFiles.setState({ startScene: path });
    logEditor('info', `Start scene: ${path}`);
  } catch (error) {
    logEditor('error', `Setting the start scene failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

/** Assets panel entries for prefab and scene files. */
export function prefabSceneItems(path: string): MenuItem[] {
  if (path.endsWith('.prefab.json')) {
    const prefabs = usePrefabs.getState();
    return [
      { id: 'prefab-open', label: 'Open Prefab', icon: 'prefab', run: () => void prefabs.openStage(path, 'isolated') },
      { id: 'prefab-variant', label: 'Create Prefab Variant', icon: 'copy', run: () => void prefabs.createVariantOf(path) },
    ];
  }
  if (path.endsWith('.scene.json')) {
    const active = useScene.getState().path === path;
    const added = useSceneSet.getState().scenes.some((s) => s.path === path);
    const start = useProjectFiles.getState().startScene === path;
    return [
      { id: 'scene-additive', label: 'Open Scene Additive', icon: 'layers', disabled: active || added, run: () => void useSceneSet.getState().add(path, true) },
      { id: 'scene-start', label: start ? 'Start Scene (current)' : 'Set as Start Scene', icon: 'play', disabled: start, run: () => void setStartScene(path) },
    ];
  }
  return [];
}

/** Hierarchy entries for an entity: Create Prefab, or the instance actions. */
export function hierarchyPrefabItems(id: string, count: number): MenuItem[] {
  const doc = useScene.getState().doc;
  if (!doc) return [];
  const prefabs = usePrefabs.getState();
  const own = doc.entities.find((e) => e.id === id);
  const ref = prefabRef(own);
  const items: MenuItem[] = [{ id: 'sep-prefab', label: '', separator: true }];
  if (own && !ref) {
    items.push({
      id: 'prefab-create',
      label: count > 1 ? `Create ${count} Prefabs` : 'Create Prefab',
      icon: 'prefab',
      run: () => void (count > 1 ? prefabs.createFromSelection() : prefabs.createFromEntity(id)),
    });
    return items;
  }
  if (own && ref) {
    items.push(
      { id: 'prefab-h', label: 'Prefab', heading: true },
      { id: 'prefab-open', label: 'Open Prefab', icon: 'prefab', run: () => void prefabs.openStage(ref.path, 'isolated') },
      { id: 'prefab-context', label: 'Open Prefab in Context', icon: 'layers', run: () => void prefabs.openStage(ref.path, 'context') },
      { id: 'prefab-asset', label: 'Select Prefab Asset', icon: 'target', run: () => useAssets.getState().pingAsset(ref.path) },
      { id: 'prefab-apply', label: 'Apply All Overrides', icon: 'arrowUp', run: () => void prefabs.applyOverrides(id) },
      { id: 'prefab-revert', label: 'Revert All Overrides', icon: 'undo', run: () => prefabs.revertOverrides(id) },
      { id: 'prefab-unpack', label: 'Unpack Prefab', icon: 'cube', run: () => prefabs.unpack(id, false) },
      { id: 'prefab-unpack-all', label: 'Unpack Prefab Completely', icon: 'cube', run: () => prefabs.unpack(id, true) },
    );
    return items;
  }
  const owner = prefabInstanceOf(doc, id);
  if (owner) {
    const path = prefabRef(owner.host)!.path;
    items.push(
      { id: 'prefab-h', label: 'Prefab', heading: true },
      { id: 'prefab-root', label: 'Select Prefab Root', icon: 'target', run: () => useScene.getState().select([owner.host.id]) },
      { id: 'prefab-open', label: 'Open Prefab', icon: 'prefab', run: () => void prefabs.openStage(path, 'isolated') },
      {
        id: 'prefab-revert-entity',
        label: 'Revert Overrides on this Entity',
        icon: 'undo',
        run: () => {
          const items = (owner.host.components?.prefab as { overrides?: { target: string; path?: string }[] } | undefined)?.overrides ?? [];
          const mine = items
            .filter((o) => o.target === owner.target)
            .map((o) => ({ id, target: o.target, path: o.path ?? '', root: false, removed: false, label: '' }));
          prefabs.revertOverrides(owner.host.id, mine);
        },
      },
    );
  }
  return items;
}
